import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useActiveInteraction, useElapsedSeconds } from "@/lib/hooks";
import { fmtClock, fmtDuration } from "@/lib/utils";
import { Square } from "lucide-react";
import { toast } from "sonner";

/**
 * Barra global "a interação continua a contar" — fica visível em qualquer
 * página enquanto o utilizador tiver um cronómetro de cliente em curso.
 */
export function InteractionBar() {
  const qc = useQueryClient();
  const { data } = useActiveInteraction();
  const active = data?.interaction;
  const elapsed = useElapsedSeconds(active?.startedAt);

  if (!active) return null;

  const stop = async () => {
    try {
      const { data: closed } = await api.post("/interactions/stop");
      qc.invalidateQueries({ queryKey: ["interaction-active"] });
      qc.invalidateQueries({ queryKey: ["interactions"] });
      toast.success(`Interação em ${active.clientName} terminada · ${fmtDuration(closed.minutes)}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-40 flex justify-center px-4">
      <div
        className="pointer-events-auto flex max-w-full items-center gap-3 rounded-full border border-primary/30 bg-card/95 py-2 pl-4 pr-2 shadow-lg backdrop-blur"
        data-testid="interaction-bar"
      >
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
        </span>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-poppins font-semibold">{active.clientName}</div>
          <div className="truncate text-[11px] text-muted-foreground max-w-[220px]">
            {active.taskTitle || active.description || "Sem descrição"}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 font-mono text-sm tabular-nums text-primary">
          {fmtClock(elapsed)}
        </span>
        <button
          type="button"
          onClick={stop}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-80"
          title="Parar interação"
          aria-label="Parar interação"
          data-testid="interaction-stop"
        >
          <Square className="h-3.5 w-3.5 fill-current" />
        </button>
      </div>
    </div>
  );
}
